import { safeGetItem, safeRemoveItem, safeSetItem } from '@/lib/utils/safe-storage'
import {
  ACQUISITION_WINDOW_DAYS,
  ACQUISITION_SOURCES,
  ACQUISITION_MEDIUMS,
  ACQUISITION_CAMPAIGNS,
  acquisitionLandingPath,
} from './acquisition'

export const ACQUISITION_TOUCH_KEY = 'alumnia-acquisition-touch'
export const ACQUISITION_DEDUP_KEY = 'alumnia-acquisition-attributed'
export const ACQUISITION_WINDOW_MS = ACQUISITION_WINDOW_DAYS * 86_400_000
export interface AcquisitionTouch {
  first_touch_at: string
  landing_path: string
  landing_release: string
  acquisition_source: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
}
export const TOUCH_FIELDS = [
  'first_touch_at',
  'landing_path',
  'landing_release',
  'acquisition_source',
  'utm_source',
  'utm_medium',
  'utm_campaign',
] as const
export const UTM_ENUMS = {
  utm_source: [...ACQUISITION_SOURCES, 'other'],
  utm_medium: [...ACQUISITION_MEDIUMS, 'other'],
  utm_campaign: [...ACQUISITION_CAMPAIGNS, 'other'],
} as const
export function boundedRelease(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length <= 40 &&
    /^(?:[a-f0-9]{7,40}|development|v?\d+\.\d+\.\d+)$/.test(value)
  )
}
export function recentTimestamp(value: unknown, now: number): value is string {
  if (typeof value !== 'string') return false
  const time = Date.parse(value)
  return (
    Number.isFinite(time) &&
    new Date(time).toISOString() === value &&
    time <= now &&
    now - time < ACQUISITION_WINDOW_MS
  )
}
export function validTouch(value: unknown, now = Date.now()): value is AcquisitionTouch {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const v = value as Record<string, unknown>
  return (
    Object.keys(v).every((key) => (TOUCH_FIELDS as readonly string[]).includes(key)) &&
    recentTimestamp(v.first_touch_at, now) &&
    boundedRelease(v.landing_release) &&
    typeof v.landing_path === 'string' &&
    (acquisitionLandingPath(v.landing_path) === v.landing_path ||
      ['/blog/[post]', '/es/blog/[post]'].includes(v.landing_path)) &&
    typeof v.acquisition_source === 'string' &&
    [...ACQUISITION_SOURCES, 'other', 'unknown'].includes(v.acquisition_source) &&
    Object.entries(UTM_ENUMS).every(
      ([key, values]) =>
        v[key] === undefined ||
        (typeof v[key] === 'string' && (values as readonly string[]).includes(v[key]))
    )
  )
}
export function clearAcquisitionTouch(): void {
  safeRemoveItem(ACQUISITION_TOUCH_KEY)
  safeRemoveItem(ACQUISITION_DEDUP_KEY)
}
export function readAcquisitionTouch(now = Date.now()): AcquisitionTouch | null {
  const raw = safeGetItem(ACQUISITION_TOUCH_KEY)
  if (!raw) return null
  try {
    const value: unknown = JSON.parse(raw)
    if (validTouch(value, now)) return value
  } catch {
    /* malformed storage is discarded */
  }
  clearAcquisitionTouch()
  return null
}
/** Called only after the tracker's trusted, visible, full-consent public engagement gate. */
export function recordAcquisitionTouch(properties: Record<string, string>, now = Date.now()): void {
  if (readAcquisitionTouch(now)) return
  const touch = { ...properties, first_touch_at: new Date(now).toISOString() }
  if (validTouch(touch, now)) safeSetItem(ACQUISITION_TOUCH_KEY, JSON.stringify(touch))
}
